import {
  collection,
  doc,
  getDoc,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import {
  AddQuizFormVariantBody,
  ConvertQuizToEmbeddedBody,
  DeleteQuizBody,
  DuplicateQuizBody,
  PublishQuizBody,
  UpdateQuizBody,
} from '@superfunnel/schemas/quiz';
import { DynamicPage } from '@superfunnel/types/dynamic-page';
import { Quiz, QuizForm } from '@superfunnel/types/quiz';

import { db, functions } from '../Firebase';
import { emptyConverter } from '../utils';
import { createPublishPreview } from './DynamicPagesService';

// ============================================================================
// Quiz Collection References
// ============================================================================

export const quizzesColRef = (accountId: string) => {
  return collection(db, 'accounts', accountId, 'quizzes').withConverter(
    emptyConverter<Quiz>(),
  );
};

export const quizzesQuery = (accountId: string) => {
  return query(
    quizzesColRef(accountId),
    where('status', 'in', ['active', 'draft']),
  );
};

export const quizRef = (accountId: string, quizId: string) => {
  return doc(quizzesColRef(accountId), quizId);
};

/**
 * Mark a Quiz and its linked DynamicPage as having changes to publish.
 * Called when form data is modified.
 */
export const markQuizHasChangesToPublish = async (
  accountId: string,
  quizId: string,
): Promise<void> => {
  // Update the quiz
  const quizDocRef = quizRef(accountId, quizId);
  await setDoc(
    quizDocRef,
    { hasChangesToPublish: true, lastEditedAt: serverTimestamp() },
    { merge: true },
  );

  // Get the linked DynamicPage ID from the quiz (only for independent quizzes)
  const quizDoc = await getDoc(quizDocRef);
  const quiz = quizDoc.data();

  // Only update DynamicPage for independent quizzes
  const linkedDynamicPageId =
    quiz && quiz.quizType !== 'embedded' && 'linkedDynamicPageId' in quiz
      ? quiz.linkedDynamicPageId
      : undefined;

  if (linkedDynamicPageId) {
    // Update the DynamicPage
    const dynamicPageRef = doc(
      db,
      'accounts',
      accountId,
      'campaigns',
      linkedDynamicPageId,
    ).withConverter(emptyConverter<DynamicPage>());

    await setDoc(
      dynamicPageRef,
      { hasChangesToPublish: true, lastEditedAt: serverTimestamp() },
      { merge: true },
    );
  }
};

// ============================================================================
// Quiz Forms Collection References
// ============================================================================

export const quizFormsColRef = (accountId: string, quizId: string) => {
  return collection(
    db,
    'accounts',
    accountId,
    'quizzes',
    quizId,
    'forms',
  ).withConverter(emptyConverter<QuizForm>());
};

export const quizFormsQuery = (accountId: string, quizId: string) => {
  return query(
    quizFormsColRef(accountId, quizId),
    where('status', 'in', ['active', 'draft']),
  );
};

// ============================================================================
// Fetch All Quiz Forms for Account
// ============================================================================

// ============================================================================
// Quiz CRUD Operations (via HTTP endpoints)
// ============================================================================

export const updateQuiz = async (
  accountId: string,
  quizId: string,
  data: Omit<UpdateQuizBody, 'accountId' | 'quizId'>,
): Promise<void> => {
  const fn = httpsCallable<UpdateQuizBody, void>(
    functions,
    'accounts/quizzes/updateQuiz',
  );

  await fn({
    accountId,
    quizId,
    ...data,
  });
};

export const deleteQuiz = async (
  accountId: string,
  quizId: string,
): Promise<void> => {
  const fn = httpsCallable<DeleteQuizBody, void>(
    functions,
    'accounts/quizzes/deleteQuiz',
  );

  await fn({ accountId, quizId });
};

export const addQuizFormVariant = async (
  body: AddQuizFormVariantBody,
): Promise<{ formId: string }> => {
  const fn = httpsCallable<AddQuizFormVariantBody, { formId: string }>(
    functions,
    'accounts/quizzes/addFormVariant',
  );

  const res = await fn(body);
  return res.data;
};

export const convertQuizToEmbedded = async (
  accountId: string,
  quizId: string,
): Promise<void> => {
  const fn = httpsCallable<ConvertQuizToEmbeddedBody, void>(
    functions,
    'accounts/quizzes/convertToEmbedded',
  );

  await fn({ accountId, quizId });
};

export interface DuplicateQuizResponse {
  quizId: string;
  dynamicPageId: string;
}

export const duplicateQuiz = async (
  accountId: string,
  quizId: string,
  name?: string,
  path?: string,
): Promise<DuplicateQuizResponse> => {
  const fn = httpsCallable<DuplicateQuizBody, DuplicateQuizResponse>(
    functions,
    'accounts/quizzes/duplicateQuiz',
  );

  const res = await fn({
    accountId,
    quizId,
    name,
    path,
  });

  return res.data;
};

/**
 * Publishes an independent quiz from a preview built by `createPublishPreview`
 * with this quiz's id. The server holds the snapshot, so what goes live is
 * exactly what the publish popover previewed.
 */
export const publishQuiz = async (
  accountId: string,
  quizId: string,
  previewId: string,
  versionName?: string,
  versionDescription?: string,
): Promise<{ versionId: string; hasPendingChanges: boolean }> => {
  const fn = httpsCallable<
    PublishQuizBody,
    { versionId: string; hasPendingChanges: boolean }
  >(functions, 'accounts/quizzes/publishQuiz', { timeout: 120000 });

  const res = await fn({
    accountId,
    quizId,
    previewId,
    versionName,
    versionDescription,
  });

  return res.data;
};

/**
 * Publishes a single quiz as part of a bulk publish. Mirrors
 * `bulkPublishDynamicPage`: the server builds the preview and publishes it, so
 * no version blob crosses the wire and the name is ours, not the model's.
 */
export const bulkPublishQuiz = async (
  accountId: string,
  quizId: string,
  dynamicPageId: string,
  versionName: string,
  versionDescription: string,
): Promise<void> => {
  const preview = await createPublishPreview(accountId, dynamicPageId, {
    quizId,
  });

  await publishQuiz(
    accountId,
    quizId,
    preview.previewId,
    versionName,
    versionDescription,
  );
};

// ============================================================================
// Quiz Form Variant Operations (A/B Testing)
// ============================================================================

// ============================================================================
// Quiz Link Replacer Operations (Bulk Update Links)
// ============================================================================

export const getAllQuizLinks = async (
  accountId: string,
  quizIds: string[],
): Promise<string[]> => {
  const fn = httpsCallable<{ accountId: string; quizIds: string[] }, string[]>(
    functions,
    'accounts/quizzes/linkReplacer/getAllLinks',
    { timeout: 600000 },
  );

  const res = await fn({ accountId, quizIds });

  return res.data;
};

export const replaceAllQuizLinks = async (
  accountId: string,
  quizIds: string[],
  mappings: { link: string; toLink: string }[],
): Promise<number> => {
  const fn = httpsCallable<
    {
      accountId: string;
      quizIds: string[];
      mappings: { link: string; toLink: string }[];
    },
    number
  >(functions, 'accounts/quizzes/linkReplacer/replaceLinks', {
    timeout: 600000,
  });

  const res = await fn({ accountId, quizIds, mappings });

  return res.data;
};
